// WithdrawalFlow — 2-step wizard for the manual withdrawal flow.
//
// Step 1 (details):  amount + your UPI id + optional WhatsApp.
//                    Live "after this" balance preview shows what
//                    will be held. Submit button disabled until
//                    valid + within available balance.
// Step 2 (confirm):  one last review (held vs spendable) + submit.
// Step 3 (success):  "Awaiting payout" with a referenced request id
//                    and what-happens-next text.
//
// On submit, the wallet is debited immediately (kind=
// 'withdrawal_hold'). The client refreshes the global wallet store
// so the NavBar chip + Wallet page balance reflect the hold within
// one render frame.

import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../lib/i18n';
import { useToast } from '../lib/toast';
import { useWallet, refreshWallet } from '../lib/wallet';
import Icon from './Icon';

type Step = 1 | 2 | 'success';

// Quick-pick chips for the amount step. Min withdrawal is ₹200,
// then double-up steps to ₹10,000. Each chip is wallet-filtered
// at render time — chips above the player's current balance are
// hidden so the grid stays honest. Manual entry below 200 is
// blocked from advancing past Step 1 (validAmount check).
const QUICK_AMOUNTS = [200, 500, 1000, 2000, 5000, 10000];
const MIN_AMOUNT = 200;

export default function WithdrawalFlow({
  open, onClose, onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}) {
  const t = useT();
  const { push } = useToast();
  const { balance } = useWallet();
  // Task D-2 — read the user's last-used UPI from auth state so we
  // can pre-fill the input when this modal opens. The user can
  // still type a fresh value, which the backend will then save as
  // the new canonical handle.
  const { user, refreshMe } = useAuth();

  const [step, setStep] = useState<Step>(1);
  const [amount, setAmount] = useState<number>(0);
  const [upiId, setUpiId] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    // Pre-fill UPI from the user's saved value (set on the last
    // successful withdrawal). Falls back to empty for a brand-new
    // player with no payout history.
    const presetUpi = (user?.saved_upi_id || '').trim();
    setStep(1); setAmount(Math.min(500, balance)); setUpiId(presetUpi); setWhatsapp('');
    setErr(null); setSubmittedId(null);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, balance, user?.saved_upi_id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Validation gates per step. Task D-2: tightened the client UPI
  // shape to mirror the server-side Zod regex
  // /^[a-z0-9._-]{2,80}@[a-z0-9]{2,40}$/i so the Next button only
  // enables on a structurally valid handle. The PSP-whitelist
  // check still happens server-side (we don't want to ship the
  // suffix list to the bundle since it's CRUD-able).
  const validUpi = /^[a-z0-9._-]{2,80}@[a-z0-9]{2,40}$/i.test(upiId.trim());
  // Whatsapp is optional; if present it must be a 10-digit Indian
  // mobile starting with 6/7/8/9, optionally +91-prefixed. Same
  // regex the server enforces.
  const validWhatsapp = whatsapp.trim() === ''
    || /^(?:\+91)?[6-9]\d{9}$/.test(whatsapp.trim().replace(/\s+/g, ''));
  const validAmount = amount >= MIN_AMOUNT && amount <= balance;
  const canNext = validAmount && validUpi && validWhatsapp;

  async function submit() {
    setBusy(true); setErr(null);
    try {
      const r = await api.post<{ request: { id: number } }>('/me/withdraw', {
        amount,
        upi_id: upiId.trim(),
        whatsapp_number: whatsapp.trim() || null,
        qr_image_url: null, // QR upload TBD; not in MVP
      });
      // Wallet was debited (held) — refresh global store so the chip
      // and dashboard balance update immediately. Task D-2:
      // refreshMe() too so the auth context's user.saved_upi_id
      // picks up the value the server just stamped — that powers
      // the next pre-fill AND the Account-tab "Saved Payment
      // Details" card without needing a page reload.
      await Promise.all([refreshWallet(), refreshMe()]);
      setSubmittedId(r.request.id);
      setStep('success');
      push('success', t('withdraw.toast.submitted'));
      onCompleted?.();
      // 2026-05-21 — Auto-close after a brief success display.
      // Reason: users on the previous build often weren't sure whether
      // their request actually went through because the modal sat on
      // the success step until they manually pressed Close. The 1.4 s
      // delay is long enough to register the green check + ref ID,
      // short enough that confusion can't set in. The pending request
      // also surfaces in the Wallet pending panel immediately via
      // onCompleted → reloadAll, so the user has a second confirmation
      // visible after the modal disappears.
      window.setTimeout(() => { onClose(); }, 1400);
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  }

  const heldAfter = amount;
  const spendableAfter = Math.max(0, balance - amount);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm animate-slide-down"
      onClick={onClose}
      role="dialog" aria-modal="true" aria-label={t('withdraw.title')}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md max-h-[92dvh] flex flex-col
                   rounded-3xl overflow-hidden
                   bg-white dark:bg-arena-800
                   border border-arena-200 dark:border-neon-400/25
                   shadow-2xl"
      >
        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-arena-200 dark:border-arena-600/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center
                            bg-gradient-to-br from-cyan-300/40 to-cyan-500/30
                            text-cyan-700 dark:text-cyan-300">
              <Icon name="account_balance_wallet" size={22} filled />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display font-extrabold text-base text-arena-800 dark:text-neon-100 leading-tight">
                {t('withdraw.title')}
              </h2>
              {step !== 'success' && (
                <p className="text-[11px] text-arena-500 dark:text-neon-200/60 leading-tight">
                  {t(`withdraw.step${step}.subtitle`)}
                </p>
              )}
            </div>
            <button onClick={onClose} aria-label={t('common.close')}
              className="shrink-0 w-9 h-9 rounded-full grid place-items-center
                         text-arena-500 dark:text-neon-200/70
                         hover:bg-arena-100 dark:hover:bg-arena-700/60">
              <Icon name="close" size={20} />
            </button>
          </div>
          {step !== 'success' && (
            <div className="mt-3 flex gap-1">
              {[1, 2].map((n) => (
                <div key={n} className={`flex-1 h-1.5 rounded-full ${
                  (step as number) >= n ? 'bg-cyan-500' : 'bg-arena-200 dark:bg-arena-700/60'
                }`} />
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3">
          {step === 1 && (
            <>
              <div className="rounded-xl bg-arena-50/70 dark:bg-arena-900/40 border border-arena-200 dark:border-arena-600/40 px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60">
                  {t('withdraw.balance')}
                </div>
                <div className="font-display font-black text-2xl text-arena-800 dark:text-neon-100 tabular-nums">
                  ₹{balance.toLocaleString('en-IN')}
                </div>
              </div>

              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60 mb-1.5">
                  {t('withdraw.amount')}
                </span>
                <div className="flex items-center gap-2 rounded-2xl border border-arena-200 dark:border-arena-600/40
                                bg-white dark:bg-arena-900/40 px-4 py-3">
                  <span className="text-2xl font-display font-black text-arena-800 dark:text-neon-100">₹</span>
                  <input
                    type="number" inputMode="numeric"
                    value={amount || ''}
                    onChange={(e) => setAmount(Math.max(0, Math.min(balance, Number(e.target.value) || 0)))}
                    placeholder="0"
                    className="flex-1 bg-transparent outline-none text-3xl font-display font-black tabular-nums text-arena-800 dark:text-neon-100"
                  />
                  <button type="button" onClick={() => setAmount(balance)}
                    className="text-xs font-bold uppercase tracking-wider text-cyan-700 dark:text-cyan-300
                               px-2 py-1 rounded-md hover:bg-cyan-100/60 dark:hover:bg-cyan-400/10">
                    {t('withdraw.max')}
                  </button>
                </div>
                {/* Validation error (rose) takes priority — only shows
                    when the user has typed something invalid. Otherwise
                    show the persistent orange "Min ₹200 withdrawal"
                    hint so the rule is visible at a glance. */}
                {!validAmount && amount > 0 ? (
                  <p className="text-[11px] text-rose-600 dark:text-rose-300 mt-1">
                    {amount < MIN_AMOUNT
                      ? t('withdraw.errMin', { min: '₹' + MIN_AMOUNT })
                      : t('withdraw.errExceed')}
                  </p>
                ) : (
                  <p className="text-[11px] mt-1 font-bold text-brand-600 dark:text-brand-400">
                    {t('withdraw.minHint', { min: '₹' + MIN_AMOUNT })}
                  </p>
                )}
              </label>

              <div className="grid grid-cols-3 gap-2">
                {QUICK_AMOUNTS.filter((a) => a <= balance).map((a) => (
                  <button key={a} type="button" onClick={() => setAmount(a)}
                    className={`py-2 rounded-xl font-bold tabular-nums text-sm border transition active:scale-[.98] ${
                      amount === a
                        ? 'bg-cyan-600 border-cyan-700 text-white shadow'
                        : 'bg-white dark:bg-arena-900/40 border-arena-200 dark:border-arena-600/40 text-arena-800 dark:text-neon-100 hover:border-cyan-400/50'
                    }`}>
                    ₹{a.toLocaleString('en-IN')}
                  </button>
                ))}
              </div>

              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60 mb-1.5">
                  {t('withdraw.upiId')}
                  {user?.saved_upi_id && (
                    <span className="ml-2 normal-case font-normal text-emerald-600 dark:text-emerald-400">
                      <Icon name="bookmark" size={12} className="inline -mt-0.5" /> {t('withdraw.upiSaved')}
                    </span>
                  )}
                </span>
                <input
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value.replace(/\s+/g, '').slice(0, 120))}
                  placeholder={t('withdraw.upiIdPh')}
                  className="w-full font-mono text-sm bg-white dark:bg-arena-900/40 border border-arena-300 dark:border-arena-600/50
                             rounded-xl px-3 py-3 outline-none focus:border-cyan-400"
                  maxLength={120}
                />
                {/* Inline shape error wins over the help text once
                    the user has typed something invalid. */}
                {upiId.trim() !== '' && !validUpi ? (
                  <p className="text-[11px] text-rose-600 dark:text-rose-300 mt-1">
                    {t('withdraw.upiShapeErr')}
                  </p>
                ) : (
                  <p className="text-[11px] text-arena-500 dark:text-neon-200/60 mt-1">
                    {t('withdraw.upiIdHelp')}
                  </p>
                )}
              </label>

              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60 mb-1.5">
                  {t('withdraw.whatsapp')} <span className="font-normal text-arena-400">{t('common.optional')}</span>
                </span>
                <input
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value.slice(0, 40))}
                  placeholder="9876543210"
                  inputMode="numeric"
                  className="w-full text-sm bg-white dark:bg-arena-900/40 border border-arena-300 dark:border-arena-600/50
                             rounded-xl px-3 py-3 outline-none focus:border-cyan-400"
                  maxLength={40}
                />
                {whatsapp.trim() !== '' && !validWhatsapp && (
                  <p className="text-[11px] text-rose-600 dark:text-rose-300 mt-1">
                    {t('withdraw.phoneShapeErr')}
                  </p>
                )}
              </label>
            </>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="rounded-2xl border border-arena-200 dark:border-neon-400/25
                              bg-white/70 dark:bg-arena-900/40 p-4 space-y-3">
                <h3 className="font-display font-extrabold text-sm text-arena-800 dark:text-neon-100 mb-1">
                  {t('withdraw.step2.review')}
                </h3>
                <ReviewRow label={t('withdraw.amount')} value={'₹' + amount.toLocaleString('en-IN')} hot />
                <ReviewRow label={t('withdraw.upiId')} value={upiId} mono />
                {whatsapp && <ReviewRow label={t('withdraw.whatsapp')} value={whatsapp} />}
              </div>

              <div className="rounded-2xl border border-amber-300 dark:border-amber-400/30
                              bg-amber-50/70 dark:bg-amber-900/15 p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <Icon name="info" size={18} filled className="text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
                  <div className="text-[12px] text-amber-800 dark:text-amber-200">
                    {t('withdraw.step2.heldNotice')}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="rounded-lg bg-white dark:bg-arena-900/40 px-3 py-2 border border-amber-200 dark:border-amber-400/20">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60">
                      {t('withdraw.heldAfter')}
                    </div>
                    <div className="font-display font-black text-lg text-amber-700 dark:text-amber-300 tabular-nums">
                      ₹{heldAfter.toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white dark:bg-arena-900/40 px-3 py-2 border border-amber-200 dark:border-amber-400/20">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60">
                      {t('withdraw.spendableAfter')}
                    </div>
                    <div className="font-display font-black text-lg text-emerald-700 dark:text-emerald-300 tabular-nums">
                      ₹{spendableAfter.toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-arena-500 dark:text-neon-200/60 leading-relaxed">
                {t('withdraw.step2.disclaimer')}
              </p>

              {err && (
                <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-400/30
                                text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">
                  {err}
                </div>
              )}
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-3 space-y-3">
              <div className="mx-auto w-16 h-16 rounded-2xl grid place-items-center
                              bg-cyan-100 dark:bg-cyan-400/15
                              border border-cyan-300 dark:border-cyan-400/30
                              text-cyan-700 dark:text-cyan-300">
                <Icon name="hourglass_top" size={32} filled />
              </div>
              <h3 className="font-display font-extrabold text-lg text-arena-800 dark:text-neon-100">
                {t('withdraw.success.title')}
              </h3>
              <p className="text-sm text-arena-600 dark:text-neon-200/80">
                {t('withdraw.success.body', { amount: '₹' + amount.toLocaleString('en-IN') })}
              </p>
              {submittedId != null && (
                <div className="text-[11px] text-arena-500 dark:text-neon-200/60">
                  {t('withdraw.success.refId')}: #<b className="font-mono">{submittedId}</b>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-arena-200 dark:border-arena-600/40 shrink-0 flex items-center gap-2">
          {step === 1 && (
            <>
              <button type="button" onClick={onClose}
                className="px-4 py-2.5 rounded-full text-sm font-bold text-arena-700 dark:text-neon-200
                           bg-arena-100 dark:bg-arena-700/60 hover:bg-arena-200 dark:hover:bg-arena-700">
                {t('common.cancel')}
              </button>
              <div className="flex-1" />
              <button type="button" onClick={() => setStep(2)}
                disabled={!canNext}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full
                           font-bold text-sm tracking-wider uppercase text-white
                           bg-cyan-600 hover:bg-cyan-500
                           border border-white/15 active:scale-[.98] transition disabled:opacity-50">
                {t('common.next')} <Icon name="arrow_forward" size={16} filled />
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button type="button" onClick={() => setStep(1)} disabled={busy}
                className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full text-sm font-bold
                           text-arena-700 dark:text-neon-200
                           bg-arena-100 dark:bg-arena-700/60 hover:bg-arena-200 dark:hover:bg-arena-700 disabled:opacity-50">
                <Icon name="arrow_back" size={16} /> {t('common.back')}
              </button>
              <div className="flex-1" />
              <button type="button" onClick={submit}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full
                           font-bold text-sm tracking-wider uppercase text-white
                           bg-cyan-600 hover:bg-cyan-500
                           border border-white/15 active:scale-[.98] transition disabled:opacity-50">
                {busy
                  ? <><Icon name="progress_activity" size={16} className="animate-spin" /> {t('common.sending')}</>
                  : <>{t('withdraw.submit')} <Icon name="check" size={16} filled /></>}
              </button>
            </>
          )}
          {step === 'success' && (
            <>
              <div className="flex-1" />
              <button type="button" onClick={onClose}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full
                           font-bold text-sm tracking-wider uppercase text-white
                           bg-cyan-600 hover:bg-cyan-500
                           border border-white/15 active:scale-[.98] transition">
                {t('common.close')} <Icon name="check" size={16} filled />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value, hot, mono }: { label: string; value: string; hot?: boolean; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[11px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60">{label}</span>
      <span className={`${mono ? 'font-mono' : 'font-bold'} text-sm ${hot ? 'text-cyan-700 dark:text-cyan-300 text-base font-display font-black' : 'text-arena-800 dark:text-neon-100'} truncate`}>
        {value}
      </span>
    </div>
  );
}
